import { RubyGemMark } from '../../../components/RubyGemMark';
import React, { useEffect, useMemo, useState } from 'react';
import { X, AlertTriangle, Save, Monitor, CreditCard, Package, Cable, Trash2, Archive, Link2, Lock, RefreshCw, Paperclip, Megaphone, Wrench, Search, ChevronRight, Columns, Smartphone, Ban, ScanLine, Usb, Printer, Scale, Signal, Wifi, Network, Camera, Fingerprint, Nfc } from 'lucide-react';
import ImageGallery from './ImageGallery';
import DocumentsPanel from './DocumentsPanel';
import SearchableDropdown from '../../../components/SearchableDropdown';
import { Toggle } from '../../../components/pos/CardProcessingOptionsCard';
import { CardSetupPolicyPanel, ReaderCapabilitiesPanel } from './CardSetupCatalogPanels';
import StatusBar, { DEVICE_STATUS_OPTIONS } from './StatusBar';
import {
  hardwareApi, money, CATEGORY_LABEL, formFactorLabel,
  type DeviceModelRow, type ModelUsage,
} from '../../../services/hardwareApi';
import {
  DEVICE_CATEGORIES, TERMINAL_FORM_FACTORS, SELECTABLE_TERMINAL_FORM_FACTORS, DEVICE_INTEGRATIONS,
  SELECTABLE_PAYMENT_CONFIGS, LEGACY_PAYMENT_CONFIGS, getPaymentConfig, resolvePaymentConfig,
} from '@platform/shared-types';
import { buildTargetOptions, devicesFor, parseHardwareIds } from './buildTargets';
import { CapabilitiesFields } from './CapabilitiesFields';
import { inventoryApi } from '../../../services/inventoryApi';
import { billingApi } from '../../../services/billingApi';
import { RubyLoader } from '../../../components/RubyLoader';

/** The P-series routes a Dejavoo terminal model may offer — one physical device, three ways to reach it. */
const P_SERIES_LANES = ['dejavoo_spin_usb', 'dejavoo_spin_cloud', 'dejavoo_rubytill_pay'] as const;

/** Approved copy, verbatim — bulk-vs-serialized-inventory.html screen 1. */
const KIND_COPY: Record<'serialized' | 'quantity', { title: string; example: string }> = {
  serialized: { title: 'Track Item Individually', example: 'ex: Devices, Terminals' },
  quantity: { title: 'Track Total Inventory', example: 'ex: Wires, Packaging, Labels' },
};

/**
 * The category picker's options, DERIVED from the shared enum rather than
 * re-listed. The hand-written copy this replaces is exactly how three new
 * categories could have been added to the schema and still not appeared here.
 * Falls back to the four originals only if the import is missing (a stale Vite
 * pre-bundle), so the picker is never empty.
 */
const CATEGORIES: readonly string[] = Array.isArray(DEVICE_CATEGORIES) && DEVICE_CATEGORIES.length
  ? DEVICE_CATEGORIES
  : ['pos_terminal', 'payment_device', 'peripheral', 'accessory'];
/**
 * The screen-type options, DERIVED from the shared list rather than re-typed —
 * the same reason CATEGORIES is derived. The fallback keeps the picker usable
 * if the runtime import is briefly missing (a stale Vite pre-bundle).
 */
/**
 * "Integrated with" — one icon per code. Guarded like every other runtime
 * shared-types read: a stale pre-bundle must not white-screen the editor over
 * a list of toggles.
 */
/** One icon per screen type, so the board reads without being parsed. */
const SCREEN_TYPE_ICON: Record<string, React.FC<any>> = {
  pos: Monitor,
  kiosk: Package,
  customer_display: Monitor,
  android_dual_screen: Columns,
  android_single_screen: Smartphone,
  kiosk_single_screen: Package,
  kiosk_dual_screen: Package,
  not_applicable: Ban,
};

/**
 * A SENSIBLE FIRST ANSWER to "is this sold to tenants?", by category.
 *
 * A terminal or a card reader is almost always sold; a cable, a poster or a
 * screwdriver almost never is. Leaving it blank made every new device stop on a
 * question whose answer was obvious from the category just above it. It is a
 * DEFAULT, not a decision — the operator can flip it, and doing so stops the
 * category from moving it again.
 */
const SELLABLE_BY_DEFAULT: Record<string, boolean> = {
  pos_terminal: true,
  payment_device: true,
  peripheral: true,
  accessory: false,
  office_supply: false,
  marketing_material: false,
  tools: false,
};

const INTEGRATION_ICON: Record<string, React.FC<any>> = {
  barcode_scanner: ScanLine,
  usb: Usb,
  usb_c: Cable,
  printer: Printer,
  scale: Scale,
  offline_sim: Signal,
  wifi: Wifi,
  ethernet: Network,
  camera: Camera,
  fingerprint_scanner: Fingerprint,
  nfc: Nfc,
};

/**
 * A FALLBACK THAT STILL WORKS.
 *
 * This degraded to `[]` on its first outing, which rendered the section with no
 * toggles at all — a guard that turns a crash into an empty box has not helped
 * anybody. The local copy is the same list; a stale pre-bundle now costs
 * nothing visible. (CATEGORIES and FORM_FACTORS above do the same.)
 */
const INTEGRATIONS_FALLBACK: readonly { code: string; label: string }[] = [
  { code: 'barcode_scanner', label: 'Barcode Scanner' },
  { code: 'usb', label: 'USB' },
  { code: 'usb_c', label: 'USB-C' },
  { code: 'printer', label: 'Printer' },
  { code: 'scale', label: 'Scale' },
  { code: 'offline_sim', label: 'Offline Mode SIM' },
  { code: 'wifi', label: 'WiFi' },
  { code: 'ethernet', label: 'Ethernet' },
  { code: 'camera', label: 'Camera' },
  { code: 'fingerprint_scanner', label: 'Fingerprint Scanner' },
  { code: 'nfc', label: 'NFC' },
];

const INTEGRATIONS: readonly { code: string; label: string }[] =
  Array.isArray(DEVICE_INTEGRATIONS) && DEVICE_INTEGRATIONS.length
    ? DEVICE_INTEGRATIONS
    : INTEGRATIONS_FALLBACK;

/**
 * EVERY value that is VALID — used to decide whether a stored answer is
 * recognised, never to draw the picker.
 */
const FORM_FACTORS: readonly string[] = Array.isArray(TERMINAL_FORM_FACTORS)
  && TERMINAL_FORM_FACTORS.length
  ? TERMINAL_FORM_FACTORS
  : ['pos', 'kiosk', 'customer_display', 'android_dual_screen', 'android_single_screen',
     'kiosk_single_screen', 'kiosk_dual_screen', 'not_applicable'];

/**
 * The values OFFERED for a new device. A strict subset: 'pos' says nothing
 * `android_single_screen` does not say better, and bare 'kiosk' cannot say how
 * many screens the machine has. Both stay valid and stay rendered when a model
 * already carries them — see the "superseded" tile below.
 */
const SELECTABLE_FORM_FACTORS: readonly string[] = Array.isArray(SELECTABLE_TERMINAL_FORM_FACTORS)
  && SELECTABLE_TERMINAL_FORM_FACTORS.length
  ? SELECTABLE_TERMINAL_FORM_FACTORS
  : ['android_single_screen', 'android_dual_screen', 'kiosk_single_screen',
     'kiosk_dual_screen', 'customer_display', 'not_applicable'];
const CATEGORY_ICON: Record<string, React.FC<any>> = {
  pos_terminal: Monitor, payment_device: CreditCard, peripheral: Package, accessory: Cable,
  office_supply: Paperclip, marketing_material: Megaphone, tools: Wrench,
};

/** Dollars in the UI, integer cents in state. Converted once each way — never round-tripped. */
const centsToInput = (c: number | undefined) => (typeof c === 'number' ? (c / 100).toFixed(2) : '');
const inputToCents = (v: string) => {
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

interface Props {
  modelId: string | null;             // null = create new
  /**
   * Create-a-variation mode: modelId null + this set = the form opens as a
   * COPY of that model (specs, vendor, app profile, compatibility, economics,
   * brand, photos all carried over) with a FRESH minted Device ID and internal
   * barcode and "(copy)" appended to the name — duplicate, then edit. E.g. a
   * white-casing listing duplicated to create the black one.
   */
  duplicateOf?: string | null;
  allModels: DeviceModelRow[];
  onClose: () => void;
  /**
   * Called after a successful save or delete.
   *
   * The argument is ADDITIVE (2026-08-30): callers that ignore it behave
   * exactly as before. The full-page host reads it to decide where to go next
   * — a sellable device continues into the financing wizard with its target
   * already chosen, a stock-only one goes back to the list.
   */
  /**
   * Called AFTER a save that the operator chose to leave on — never on every
   * save. Saving used to navigate away every time, which meant a draft you
   * wanted to keep working on threw you back to the list.
   */
  onSaved: (saved?: {
    modelId: string;
    sellable: boolean;
    /**
     * Where the operator asked to go next. `new` opens the financing wizard on
     * a fresh policy, `edit` opens THIS device's existing offers — a device
     * that already has terms must be able to change them, not be offered a
     * second policy on top of the first.
     */
    terms?: 'new' | 'edit';
  }) => void;
  /**
   * `modal` (default) is the original dialog. `page` drops the overlay and the
   * height cap so the form lays out on a route of its own — creating equipment
   * is a page now, not a popup, because a form this long should not scroll
   * inside a scroll.
   */
  variant?: 'modal' | 'page';
}

// ---------------------------------------------------------------------------
// Generated identity. The DEVICE ID is minted from the name (editable, and
// re-mintable) so an operator never has to invent `mdl_…` by hand; the
// INTERNAL BARCODE is Ruby Till's own scannable id — every piece of
// equipment gets one (stored in `sharedBarcode`, which the schema leaves
// free-form; for a quantity item it doubles as the one barcode all its
// stock shares).
// ---------------------------------------------------------------------------

const slugifyId = (name: string) =>
  (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 28);

const genModelId = (name: string, taken: Set<string>): string => {
  const base = `mdl_${slugifyId(name) || 'item'}`;
  if (!taken.has(base)) return base;
  let id = base;
  while (taken.has(id)) id = `${base}_${Math.random().toString(36).slice(2, 5)}`;
  return id;
};

/** RT + 10 digits — numeric-friendly for Code128, unique enough for a catalog. */
const genInternalBarcode = () => `RT${Date.now().toString().slice(-8)}${Math.floor(10 + Math.random() * 90)}`;

const blank = () => ({
  modelId: '', category: 'pos_terminal', brand: '', manufacturer: '', modelName: '', description: '',
  // serialized-vs-quantity-tracking wave. undefined on a brand-new item forces
  // the kind picker (screen 1) before the rest of the form appears; an
  // existing model that predates this field resolves to 'serialized' on load
  // — see the load effect below.
  trackingMode: undefined as ('serialized' | 'quantity' | undefined),
  // Every NEW piece of equipment starts with a minted Ruby Till internal
  // barcode (editable / re-mintable). Existing models load their stored one.
  sharedBarcode: genInternalBarcode(),
  reorderPoint: undefined as number | undefined,
  specs: {} as Record<string, any>,
  media: { heroImageUrl: '', photos: [] as string[], videos: [] as string[] },
  documents: [] as { name: string; url: string; contentType?: string; sizeBytes?: number; uploadedAt?: number }[],
  economics: { unitCostCents: 0, inboundShippingCents: 0, dutyCents: 0, defaultOutboundShippingCents: 0 },
  vendor: { supplierName: '', supplierSku: '', leadTimeDays: undefined as number | undefined, moq: undefined as number | undefined },
  // `deviceType` is deliberately NOT seeded: nothing reads it (see the note in
  // the App profile section), and the save step strips empty strings anyway, so
  // seeding one only risks writing a phantom value onto every new device.
  integrations: [] as string[],
  // `buildTarget` is deliberately NOT seeded: it is filled the moment a screen
  // type is picked (see `pickScreenType`), and a seeded value would be a build
  // nobody chose, saved onto a device nobody checked.
  buildTarget: '',
  appProfile: { terminalFormFactor: '', otaChannel: 'stable', minAppVersion: '' },
  compatibility: { worksWith: [] as string[], requires: [] as string[] },
  // Someone filling in this form means to USE the device. 'draft' is an explicit
  // choice for a scratch entry, not the thing you land on by accident and then
  // wonder why the device never appears in a bundle.
  lifecycle: { status: 'active', replacedByModelId: '', warrantyMonths: undefined as number | undefined },
  displayOrder: 100,
  /**
   * Is this sold to tenants at all? Undefined until answered — a NEW device
   * must be asked, and an existing one that predates the field carries none.
   * See `DeviceModelSchema.sellable`.
   */
  /**
   * A NEW DEVICE DEFAULTS TO STOCK ONLY. Stock is the answer that cannot do
   * damage: a device that lands in inventory and is never offered is a row
   * somebody flips later, while one that defaults to sellable is offered to
   * tenants because nobody looked. The category still moves it when the
   * operator picks one (see SELLABLE_BY_DEFAULT) — until they answer by hand.
   *
   * `undefined` still means UNANSWERED on an EXISTING device — every device
   * predates this field, and coercing those to `false` on open would silently
   * mark things stock-only that are being sold today.
   */
  sellable: false as (boolean | undefined),
});

const DeviceEditor: React.FC<Props> = ({
  modelId, duplicateOf, allModels, onClose, onSaved, variant = 'modal',
}) => {
  const [form, setForm] = useState<any>(blank());
  const [loading, setLoading] = useState(!!modelId || !!duplicateOf);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usage, setUsage] = useState<ModelUsage | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Best-effort signal for the "locked once stock exists" chip (screen 1's
  // note). The server is the real enforcement — a 409 on save is still
  // surfaced verbatim regardless of what this guessed.
  const [hasStock, setHasStock] = useState(false);
  // True once the operator hand-edits the Device ID — auto-minting from the
  // name stops the moment they take over.
  const [idTouched, setIdTouched] = useState(false);
  // Pick-lists: brands from the Brands section, vendors from the Vendors
  // section — both best-effort (an empty list just means free choice is
  // narrower, never an error).
  const [brandList, setBrandList] = useState<string[]>([]);
  const [vendorList, setVendorList] = useState<{ vendorId: string; name: string; leadTimeDays?: number }[]>([]);
  /** Seeded with the typed name when the operator adds a vendor from here. */
  const [newVendor, setNewVendor] = useState<string | null>(null);
  /**
   * WHAT THE LAST SAVE WAS — drives the confirmation, and nothing else.
   * `null` while editing; the form is never unmounted by a save.
   */
  const [saved, setSaved] = useState<null | { kind: 'draft' | 'published'; modelId: string; sellable: boolean }>(null);
  /** Search + "only what I picked" over the compatibility list. */
  const [compatQuery, setCompatQuery] = useState('');
  const [compatOnlyPicked, setCompatOnlyPicked] = useState(false);
  /** Category pill filter in Works with. '' = All categories. */
  const [compatCategory, setCompatCategory] = useState('');
  /** Once the operator answers "sold to tenants?", the category stops guessing. */
  const [sellableTouched, setSellableTouched] = useState(false);
  /**
   * HOW MANY SELLING TERMS THIS DEVICE HAS — device_finance SKUs pointing at it.
   *
   * A device sold to tenants is not finished until somebody has said how it is
   * sold, so this is what gates publishing. `null` means UNKNOWN (a new device
   * before its first save, or a failed lookup) and never blocks: refusing to
   * publish because a list call timed out would be worse than publishing.
   */
  const [offerCount, setOfferCount] = useState<number | null>(null);

  /**
   * Re-read on every save too, not just on mount: the operator leaves for the
   * financing wizard and comes back, and the answer to "can this publish yet"
   * must not be the one from before they set the terms.
   */
  const loadOffers = React.useCallback((id: string | null) => {
    if (!id) { setOfferCount(0); return; }
    billingApi.listSkus()
      .then((r) => setOfferCount(
        (r.items || []).filter((s) => s.type === 'device_finance' && s.modelId === id).length,
      ))
      .catch(() => setOfferCount(null));
  }, []);

  useEffect(() => { loadOffers(modelId || null); }, [modelId, loadOffers]);

  useEffect(() => {
    let cancelled = false;
    inventoryApi.listBrands().then((r) => { if (!cancelled) setBrandList(r.items || []); }).catch(() => {});
    inventoryApi.listVendors().then((r) => { if (!cancelled) setVendorList((r.items || []).filter((v: any) => v.active !== false)); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!modelId && duplicateOf) {
      // Duplicate-for-edit: load the source, carry everything over, and
      // refresh ONLY what must be unique — the Device ID, the internal
      // barcode, and the name (marked "(copy)" so nobody saves an identical
      // pair unnoticed). Server-derived and per-row fields never copy.
      let cancelled = false;
      setLoading(true);
      (async () => {
        try {
          const res = await hardwareApi.getModel(duplicateOf);
          if (cancelled) return;
          const src: any = res.model;
          const copyName = `${src.modelName} (copy)`;
          const { createdAt, updatedAt, landedCostCents, ...carry } = src;
          setForm({
            ...blank(),
            ...carry,
            trackingMode: src.trackingMode || 'serialized',
            modelName: copyName,
            modelId: genModelId(copyName, new Set(allModels.map((m) => m.modelId))),
            sharedBarcode: genInternalBarcode(),
            brand: src.brand || '',
            vendor: { ...blank().vendor, ...(src.vendor || {}) },
            appProfile: { ...blank().appProfile, ...(src.appProfile || {}) },
            // A fresh variation starts its own lifecycle — never inherits the
            // source's retirement/replacement state.
            lifecycle: { status: 'active', replacedByModelId: '', warrantyMonths: src.lifecycle?.warrantyMonths },
          });
        } catch (e: any) {
          if (!cancelled) setError(e?.message || 'Failed to load the device to duplicate');
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
      setHasStock(false);
      return () => { cancelled = true; };
    }
    if (!modelId) { setForm(blank()); setLoading(false); setHasStock(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await hardwareApi.getModel(modelId);
        if (cancelled) return;
        const resolvedMode: 'serialized' | 'quantity' = res.model.trackingMode || 'serialized';
        setForm({
          ...blank(), ...res.model, trackingMode: resolvedMode,
          sharedBarcode: res.model.sharedBarcode || '',
          brand: res.model.brand || '',
          vendor: { ...blank().vendor, ...(res.model.vendor || {}) },
          appProfile: { ...blank().appProfile, ...(res.model.appProfile || {}) },
        });
        // Fire-and-forget: whichever ledger this kind actually uses.
        (resolvedMode === 'quantity'
          ? inventoryApi.stockMovements(modelId, { limit: 1 }).then((r) => (r.items?.length || 0) > 0)
          : hardwareApi.listUnits({ modelId, limit: 1 }).then((r) => (r.items?.length || 0) > 0)
        ).then((got) => { if (!cancelled) setHasStock(got); }).catch(() => { if (!cancelled) setHasStock(false); });
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load device');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    // Usage decides whether delete is even offered. Loaded separately so a slow
    // reference scan never delays the form itself.
    hardwareApi.getModelUsage(modelId)
      .then((u) => { if (!cancelled) setUsage(u); })
      .catch(() => { if (!cancelled) setUsage(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, duplicateOf]);

  const doDelete = async () => {
    if (!modelId) return;
    setDeleting(true);
    setError(null);
    try {
      await hardwareApi.deleteModel(modelId);
      onSaved();
    } catch (e: any) {
      setError(e?.message || 'Failed to delete device');
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const retire = () => {
    // Retiring needs a replacement, so drop the user into that choice rather
    // than failing on save.
    set('lifecycle.status', 'eol');
    set('lifecycle.eolDate', Date.now());
  };

  const set = (path: string, value: any) => {
    setForm((f: any) => {
      const next = { ...f };
      const parts = path.split('.');
      let cur: any = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  /**
   * The build is DERIVED from the screen type and the integrations, so picking
   * a screen type is the whole of answering it — there is no second control to
   * keep in step and nothing to pre-fill. See `resolveBuildTarget`.
   */
  const pickScreenType = (ff: string) => set('appProfile.terminalFormFactor', ff);

  /**
   * Hardware ids are edited as ONE line of text and stored as a list.
   *
   * Held separately from `form` so a half-typed "TC-TOUCH-K10, TC" does not
   * become two ids the moment a comma is pressed — the split happens on blur,
   * when the operator has finished. Re-seeded whenever a different model is
   * loaded; the `join` is stable, so this does not fight their own typing.
   */
  const storedHardwareIds = (form.appProfile?.hardwareIds || []).join(', ');
  const [hardwareIdsText, setHardwareIdsText] = useState(storedHardwareIds);
  const [hardwareIdsSeed, setHardwareIdsSeed] = useState(storedHardwareIds);
  if (storedHardwareIds !== hardwareIdsSeed) {
    setHardwareIdsSeed(storedHardwareIds);
    setHardwareIdsText(storedHardwareIds);
  }

  /**
   * The builds on offer, PLUS whatever this model already carries. A stored
   * target that has since been retired from the list must never vanish from the
   * picker — the device would read as having no build configured, which is a
   * different and much louder thing than "on an old build".
   */
  const buildTargetChoices = useMemo(
    () => buildTargetOptions(form.buildTarget).map((t) => ({
      value: t.id,
      label: t.displayName,
      subtitle: t.supportedDevices.length ? t.supportedDevices.join(' · ') : undefined,
    })),
    [form.buildTarget],
  );
  /** The devices the CHOSEN build runs on — shown under the picker, not inside it. */
  const buildDevices = devicesFor(form.buildTarget);
  /**
   * The BUILD question is a POS-TERMINAL question, and only for a screen type
   * that actually runs the POS app. A card reader is a peripheral of a terminal,
   * not something we install an APK onto; a customer display runs the
   * advertising app; `not_applicable` never registers at all. An existing answer
   * still shows regardless, so nothing an operator entered is hidden from them.
   */
  const buildTargetApplies = form.category === 'pos_terminal' || !!form.buildTarget;

  /**
   * Does this device RUN the POS app? Narrower than `buildTargetApplies`: the
   * Build row shows for every terminal because saying WHY there is no build is
   * more use than the row silently vanishing, but a customer display never
   * checks in and never takes a boot animation, so asking about either on one
   * collects an answer nothing will ever read.
   */
  const posAppApplies = buildTargetApplies
    && form.appProfile?.terminalFormFactor !== 'customer_display'
    && form.appProfile?.terminalFormFactor !== 'not_applicable';

  const landed = useMemo(
    () => (form.economics?.unitCostCents || 0) + (form.economics?.inboundShippingCents || 0) + (form.economics?.dutyCents || 0),
    [form.economics],
  );

  const isRetired = form.lifecycle?.status === 'eol' || form.lifecycle?.status === 'discontinued';
  // Mirrors the server-side refine so the user learns before the round-trip.
  const needsReplacement = isRetired && !form.lifecycle?.replacedByModelId;
  const isQuantity = form.trackingMode === 'quantity';
  // Editing an existing item that already has stock behind it locks the
  // picker — the server refuses the direction change anyway (409), this is
  // just the UI not offering a control that will bounce.
  const kindLocked = !!modelId && hasStock;
  // A brand-new item must pick its kind (screen 1) before the rest of the
  // form — and therefore Save — is reachable. Manufacturer is no longer a
  // save blocker for EITHER kind: the save step defaults it (brand, then
  // name) so the schema's `manufacturer.min(1)` still passes — an operator
  // saving a draft should never be silently stuck on a field they can fill
  // later. What still blocks is listed in `saveBlockers` and SHOWN in the
  // footer, never a mystery-disabled button.
  const saveBlockers: string[] = [];
  if (!form.trackingMode) saveBlockers.push('pick a kind');
  if (!form.modelName) saveBlockers.push('name it');
  if (!form.modelId) saveBlockers.push('give it a Device ID');
  if (needsReplacement) saveBlockers.push('choose a replacement (retired model)');
  // Only a NEW device is forced to answer. An existing one may predate the
  // field, and blocking an unrelated edit until someone classifies a cable
  // would be a save that punishes the wrong person.
  if (!modelId && typeof form.sellable !== 'boolean') saveBlockers.push('say whether it is sold to tenants');
  /**
   * SCREEN TYPE IS REQUIRED ON A POS DEVICE, and only there.
   *
   * It decides what a terminal registers AS, and leaving it blank means the
   * next person to register one guesses. `Not Applicable` is the answer for a
   * POS-category item that never becomes a terminal — required does not mean
   * "must be a screen", it means "must be answered".
   *
   * Not enforced on an EXISTING device: every device predates this rule, and
   * blocking an unrelated edit until somebody classifies an old cable is a save
   * that punishes the wrong person.
   */
  if (!modelId && form.category === 'pos_terminal' && !form.appProfile?.terminalFormFactor) {
    saveBlockers.push('choose a screen type');
  }
  /**
   * PAYMENT CONFIG IS REQUIRED ON A PAYMENT DEVICE, and only there.
   *
   * It decides how the device registers — which fields the order rail asks the
   * operator for, and which lane takes the card. Left blank, the backend used
   * to fall back to matching the model NAME and answered `cloud` for anything
   * it did not recognise exactly, so a Dejavoo called "Dejavoo P17 Android Pin
   * Pad" became an NMI cloud device. `No Payment Config (yet)` is the answer
   * for a device in for testing — required does not mean "must have a lane",
   * it means "must be answered".
   *
   * Same shape as the screen-type rule above, for the same reason: NEW devices
   * only. Every row in the catalog predates this field, and blocking an
   * unrelated edit until somebody classifies an old reader punishes the wrong
   * person.
   */
  if (!modelId && form.category === 'payment_device' && !form.paymentConfigId) {
    saveBlockers.push('choose a payment config');
  }
  const canSave = saveBlockers.length === 0 && !saving;
  /**
   * Once anything has been written, this form is EDITING that row — the id is
   * minted and `upsertModel` is keyed on it. Saying "Save" again invites the
   * fear of creating a duplicate, so it says what it does.
   */
  const alreadyWritten = !!modelId || !!saved;

  /**
   * A DRAFT NEEDS A NAME AND AN ID, AND NOTHING ELSE.
   *
   * Everything in `saveBlockers` is a requirement of a FINISHED device — the
   * kind it is tracked as, whether it is sold, a replacement for a retired
   * model. None of them has to be answered to write down "the new Dejavoo
   * terminal" and come back to it.
   */
  const draftBlockers: string[] = [];
  if (!form.modelName) draftBlockers.push('name it');
  if (!form.modelId) draftBlockers.push('give it a Device ID');
  const canSaveDraft = draftBlockers.length === 0 && !saving;
  const isDraft = form.lifecycle?.status === 'draft';

  /**
   * PUBLISHING A SELLABLE DEVICE REQUIRES SELLING TERMS.
   *
   * "Sold to tenants" with no offer behind it is a device nobody can actually
   * be sold — it lands in bundles and deals as something with no price, no
   * term and no agreement. So the order is fixed: create it, set the terms,
   * then publish. Until the terms exist the primary action is not Publish at
   * all, it is the trip to the wizard, and the row stays a draft.
   *
   * Only the PUBLISH step is gated. Editing an already-published device still
   * says Save changes and saves — an old device that predates this rule must
   * not have an unrelated edit held hostage. `offerCount === null` is unknown
   * and never blocks.
   */
  const publishing = !modelId || isDraft;
  /**
   * Sellable with nothing to sell it on. The NEXT STEP is the wizard whatever
   * state the row is in — so the bar carries Save draft and that one action,
   * never a bare "Save changes" that leaves the device unsellable.
   *
   * On a draft or a new device the trip saves a DRAFT; on a device that is
   * already live it saves normally. Publishing a live device back down into a
   * draft because it was missing terms would take it out of the catalog, which
   * is the opposite of the repair.
   */
  const needsSellingTerms = form.sellable === true && offerCount === 0;
  /**
   * Known to have offers. `null` is UNKNOWN (the lookup failed), and an unknown
   * count must route to a NEW policy: the wizard's edit mode refuses to open on
   * a device with nothing to edit, so guessing "edit" strands the operator on
   * an error page.
   */
  const hasTerms = offerCount !== null && offerCount > 0;

  /**
   * A DRAFT SAVE IS A QUIET CONFIRMATION, not a dialog. It fades after a few
   * seconds because it interrupts nothing — the operator is still typing.
   * PUBLISHING is not auto-dismissed: it is the end of a piece of work and
   * deserves an explicit "what next".
   */
  useEffect(() => {
    if (saved?.kind !== 'draft') return undefined;
    const t = setTimeout(() => setSaved(null), 4000);
    return () => clearTimeout(t);
  }, [saved]);

  const others = allModels.filter((m) => m.modelId !== form.modelId);
  const attachable = others.filter((m) => m.category !== 'pos_terminal');

  /**
   * What the compatibility list actually shows. Matching on name, brand AND
   * category is deliberate: an operator looking for "the Dejavoo printer"
   * knows one of the three, rarely all of them.
   */
  const visibleAttachable = useMemo(() => {
    const q = compatQuery.trim().toLowerCase();
    const picked = new Set(form.compatibility?.worksWith || []);
    return attachable.filter((m) => {
      if (compatOnlyPicked && !picked.has(m.modelId)) return false;
      if (compatCategory && m.category !== compatCategory) return false;
      if (!q) return true;
      return [m.modelName, m.brand, CATEGORY_LABEL[m.category], m.manufacturer]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachable, compatQuery, compatOnlyPicked, compatCategory, form.compatibility?.worksWith]);

  /**
   * The pill row under the search box. Only categories that actually have a
   * device are offered — a pill that filters to nothing is a dead end — and
   * each carries its count so the operator can see where the list is before
   * clicking. Ordered by CATEGORIES so the row does not reshuffle as the
   * catalog grows.
   */
  const compatCategoryPills = useMemo(() => {
    const counts = new Map<string, number>();
    attachable.forEach((m) => counts.set(m.category, (counts.get(m.category) || 0) + 1));
    return CATEGORIES.filter((c) => counts.has(c)).map((c) => ({
      value: c,
      label: CATEGORY_LABEL[c] || c,
      count: counts.get(c) || 0,
    }));
  }, [attachable]);

  const toggleWorksWith = (id: string) => {
    const cur: string[] = form.compatibility?.worksWith || [];
    set('compatibility.worksWith', cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };

  /**
   * SAVE — as a finished device, or as a DRAFT.
   *
   * A draft exists so an operator can stop. Entering equipment means gathering
   * specs, a barcode, a supplier and a price, and some of it is on a box in
   * another room; a form that can only be saved complete makes the operator
   * choose between inventing values and losing what they typed. Both are worse
   * than a half-filled row nobody has published.
   *
   * A draft is `lifecycle.status = 'draft'`, which every reader already
   * understands: `bundles.decorate` flags a draft component and refuses to let
   * an ACTIVE bundle contain one, so a half-entered device cannot leak into
   * something sellable just because it exists.
   *
   * The only thing a draft must have is a NAME and an id, because a row nobody
   * can identify is not a saved draft, it is litter.
   */
  const save = async (asDraft = false, thenSellingTerms: false | 'new' | 'edit' = false) => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...form,

        // Never a save blocker: default manufacturer from the brand, then the
        // name, so `DeviceModelSchema.manufacturer.min(1)` always passes.
        manufacturer: form.manufacturer || form.brand || form.modelName,
        // A draft may not have answered the kind question yet. `serialized` is
        // the schema's own default for an absent value, so writing it here
        // changes nothing about how the row reads — it just lets the draft save.
        trackingMode: form.trackingMode || (asDraft ? 'serialized' : form.trackingMode),
        brand: form.brand || undefined,
        /*
          STORED because the install panel and the fleet read it, DERIVED
          because nobody types it. Writing the resolved value on every save is
          what keeps the stored copy honest: change a device from single to
          dual screen and its build follows, with no second field for somebody
          to forget.

          NULL, not undefined, when it does not resolve. `upsertModel` merges
          this request over the stored row and JSON drops an undefined key on
          the wire, so `undefined` would send nothing and a build that no
          longer applies would quietly survive the save.
        */
        buildTarget: form.buildTarget || null,
        /* Bulk items only — the field is not asked of a serialized model, so
           saving one would persist a minted code nobody can scan. `undefined`
           (never null) because the upsert MERGES: a model that already holds
           a barcode from before this gate keeps it rather than being blanked
           by a save that simply stopped asking the question. */
        sharedBarcode: isQuantity ? (form.sharedBarcode || undefined) : undefined,
        reorderPoint: typeof form.reorderPoint === 'number' ? form.reorderPoint : undefined,
        // Drop empty optional strings so they are not persisted as ''
        appProfile: Object.fromEntries(Object.entries(form.appProfile || {}).filter(([, v]) => v !== '' && v !== undefined)),
        vendor: Object.fromEntries(Object.entries(form.vendor || {}).filter(([, v]) => v !== '' && v !== undefined)),
        // A draft stays a draft until somebody publishes it. Saving a FINISHED
        // device never silently activates one either — an operator who set the
        // lifecycle by hand keeps their answer.
        lifecycle: {
          ...Object.fromEntries(Object.entries(form.lifecycle || {}).filter(([, v]) => v !== '' && v !== undefined)),
          // Publishing must actually CLEAR draft. Editing an already-saved
          // draft loads `status: 'draft'` into the form, and spreading that
          // through unchanged meant Publish device wrote the row straight back
          // as a draft. Only that one value is promoted — a hand-set pilot,
          // eol or discontinued is the operator's answer and is left alone.
          ...(asDraft
            ? { status: 'draft' }
            : form.lifecycle?.status === 'draft' ? { status: 'active' } : {}),
        },
        media: { ...form.media, heroImageUrl: form.media?.heroImageUrl || undefined },
        // Written as a real boolean once answered, and left absent while it is
        // not — absent means UNANSWERED everywhere it is read, and coercing it
        // to `false` here would silently mark every device an operator edits
        // for some other reason as stock-only.
        sellable: typeof form.sellable === 'boolean' ? form.sellable : undefined,
      };
      await hardwareApi.upsertModel(payload);
      /**
       * THE FORM STAYS OPEN.
       *
       * Every save used to navigate — back to the list, or on into the
       * financing wizard — so saving a draft you meant to keep working on threw
       * you out of it, and publishing reloaded the page under you. The write is
       * a background call; where to go next is the operator's decision, offered
       * in the confirmation below.
       *
       * `upsertModel` is keyed on `modelId`, which is already minted, so saving
       * again UPDATES this same row rather than creating a second one. That is
       * what makes staying put safe.
       */
      /**
       * STRAIGHT ON TO THE TERMS, with no confirmation in between: the
       * operator asked for the next step, and a dialog telling them the save
       * worked before they can take it is a click that says nothing.
       */
      /**
       * THE FORM NOW HOLDS WHAT WAS WRITTEN.
       *
       * A draft save wrote `status: 'draft'` to the server but left the form
       * saying whatever it said before, so `isDraft` was false and the top bar
       * was driven by `saved` alone — which self-clears after four seconds.
       * The buttons then silently changed under the operator (Set selling
       * terms → Save changes) with nothing having happened but time passing.
       */
      setForm((f: any) => ({ ...f, lifecycle: { ...(f.lifecycle || {}), ...payload.lifecycle } }));
      if (thenSellingTerms) {
        onSaved({ modelId: payload.modelId, sellable: true, terms: thenSellingTerms });
        return;
      }
      setSaved({
        kind: asDraft ? 'draft' : 'published',
        modelId: payload.modelId,
        sellable: !asDraft && payload.sellable === true,
      });
      // The row now exists under this id, so its offers are worth asking about
      // — a first save is what turns "unknown" into a real count.
      loadOffers(payload.modelId);
    } catch (e: any) {
      setError(e?.message || 'Failed to save device');
    } finally {
      setSaving(false);
    }
  };

  const asPage = variant === 'page';

  /*
   * MODAL: centred and height-capped — the panel never exceeds the viewport,
   * the body scrolls inside it, and header + footer stay put so Save is always
   * reachable.
   *
   * PAGE: this must PASS THE HEIGHT DOWN, not merely sit inside it. The page
   * wrapper is exactly one viewport with `overflow-hidden`, so if this node is
   * allowed to size to its content the form runs past the viewport and is
   * silently clipped — unreachable, with no scrollbar to reveal it. `min-h-0`
   * is what actually permits the body below to shrink and scroll; without it a
   * flex child refuses to go below its own content height.
   */
  return (
    <div className={`dq ${asPage
      ? 'flex min-h-0 flex-1 flex-col'
      : 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'}`}>
      {/* PAGE: flush with the route — no card, no rounding, no second
          background. The form IS the page, so wrapping it in a panel put a
          page inside a page.
          MODAL: still a panel, because there it genuinely floats over one. */}
      <div className={asPage
        ? 'relative flex h-full min-h-0 w-full flex-col text-gray-900'
        : 'relative flex max-h-[92vh] w-full max-w-[1100px] flex-col overflow-hidden rounded-2xl bg-gray-50 text-gray-900 shadow-2xl'}>
        {/* ── HEADER — identity on the left, the actions on the right.
             STICKY on the page variant: this form is long enough that Save
             scrolled off the bottom, and an action you have to hunt for is one
             people stop using. The modal variant already pins its own footer. */}
        <div className="flex shrink-0 items-center justify-between bg-black px-6 py-3 text-white">
          {/* IDENTITY ONLY — where you are, and what you are editing.
              This bar previously also carried the RubyTill mark, the wordmark
              and a category · manufacturer line, stacking three rows of text
              beside the actions and leaving the bar visibly crowded. Brand is
              already established by the surrounding admin, and category and
              manufacturer are both edited in the form a few centimetres below,
              so repeating them here bought nothing but height. */}
          <div className="flex min-w-0 flex-col justify-center">
            {asPage && (
              <div className="mb-0.5 flex items-center gap-1.5 text-[10.5px] text-gray-500">
                <span>Inventory</span><span className="opacity-60">›</span>
                <span>Stock</span><span className="opacity-60">›</span>
                <span className="text-gray-300">{modelId ? 'Edit' : 'New'}</span>
              </div>
            )}
            <div className="flex min-w-0 items-center gap-2">
              <h2 className="truncate text-base font-bold">
                {modelId ? form.modelName || form.modelId : form.modelName || 'New Equipment'}
              </h2>
              {isDraft && (
                <span className="shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold tracking-wider"
                      style={{ background: '#78350F', color: '#FCD34D' }}>
                  DRAFT
                </span>
              )}
            </div>
          </div>

          {/* ── EVERY ACTION LIVES HERE.
               They used to be split: Retire and Close at the top, Save draft,
               Publish and Delete in a footer at the bottom of a form long
               enough that you could not see both at once. Which one you wanted
               depended on where you had scrolled to. One bar, always visible.
               Ordered by how destructive each is, left to right, so Publish and
               Delete are never neighbours. */}
          <div className="flex shrink-0 items-center gap-2">
            {modelId && (
              <button
                onClick={() => setConfirmDelete(true)}
                disabled={!usage?.canDelete || deleting}
                title={usage?.reason || 'Checking references…'}
                className="flex h-9 items-center gap-1.5 rounded-lg border px-2.5 text-[12.5px] font-medium disabled:opacity-40"
                style={{ borderColor: '#7F1D1D', color: '#FCA5A5', background: 'transparent' }}
              >
                <Trash2 size={13} /> Delete
              </button>
            )}
            {/* RETIRE is deliberately quiet: it withdraws the device from new
                deals and changes nothing already sold, so it needs no red
                button — and must not sit one click from Publish either. */}
            {modelId && !isRetired && (
              <button
                onClick={retire}
                title="Withdraw from new deals. Existing bundles, prices and deployed terminals keep working."
                className="flex h-9 items-center gap-1.5 rounded-lg border px-2.5 text-[12.5px] font-medium"
                style={{ borderColor: '#78350F', color: '#FCD34D', background: 'transparent' }}
              >
                <Archive size={13} /> Retire
              </button>
            )}
            <button
              onClick={onClose}
              className="h-9 rounded-lg px-2.5 text-[12.5px] font-medium text-gray-400 hover:bg-gray-800 hover:text-white"
            >
              Close
            </button>
            <span className="mx-0.5 h-5 w-px bg-gray-700" />
            <button
              onClick={() => save(true)}
              disabled={!canSaveDraft}
              title={draftBlockers.length
                ? `Still needed: ${draftBlockers.join(', ')}`
                : 'Save what you have and finish it later. It stays out of bundles and deals until published.'}
              className="flex h-9 items-center gap-1.5 rounded-lg border px-3 text-[12.5px] font-medium text-gray-200 disabled:opacity-40"
              style={{ borderColor: '#44403C', background: '#292524' }}
            >
              <Save size={13} /> {saved ? 'Save draft again' : 'Save draft'}
            </button>
            {/* SELLING TERMS — ON EVERY SAVED DEVICE, no exceptions.
                This used to appear only for a device that already had offers,
                so a device whose "sold to tenants?" question was never answered
                — which is every device predating that field — had no route to
                its terms at all. The route is the same question for all of
                them; only the label changes with what is already there.
                Omitted only when the PRIMARY button is already this action. */}
            {!!modelId && !needsSellingTerms && (
              <button
                onClick={() => save(isDraft, hasTerms ? 'edit' : 'new')}
                disabled={!canSaveDraft}
                title="Save, then open this device's selling terms and finance agreement"
                className="flex h-9 items-center gap-1.5 rounded-lg border px-3 text-[12.5px] font-medium text-gray-200 disabled:opacity-40"
                style={{ borderColor: '#44403C', background: '#292524' }}
              >
                <CreditCard size={13} />
                {hasTerms ? `Edit selling terms · ${offerCount}` : 'Set selling terms →'}
              </button>
            )}
            {/* THE PRIMARY IS THE NEXT STEP, whatever that is. For a device
                sold to tenants with no terms yet that step is the wizard, not
                Publish — publishing is what happens after the terms exist. */}
            <button
              onClick={() => (needsSellingTerms ? save(publishing, 'new') : save(false))}
              disabled={!canSave}
              title={saveBlockers.length
                ? `Still needed: ${saveBlockers.join(', ')}`
                : needsSellingTerms
                  ? 'Saves what you have and opens the financing wizard — selling terms and the finance agreement.'
                  : undefined}
              className="flex h-9 items-center gap-1.5 rounded-lg px-4 text-[12.5px] font-semibold text-white disabled:opacity-40"
              style={{ background: '#DC143C' }}
            >
              {saving ? <RubyGemMark size={14} drawing color="currentColor" /> : <Save size={14} />}
              {needsSellingTerms
                ? 'Set selling terms & agreement →'
                : isDraft || saved?.kind === 'draft' ? 'Publish device'
                  : alreadyWritten ? 'Save changes'
                    : form.sellable === false ? 'Save — stock only'
                      : 'Save device'}
            </button>
          </div>
        </div>
        {/* The letterhead's own band — the angled clip is part of the mark. */}
        <div
          className="h-[3px] shrink-0"
          style={{ background: 'linear-gradient(90deg,#e8365a,#DC143C,#9F1239)' }}
        />

        {/* WHY THE BUTTON IS DISABLED, directly under it.
            The footer that used to carry this is gone, and a reason printed
            at the far end of a long form is a reason nobody reads. */}
        {(error || (!canSave && !saving && saveBlockers.length > 0) || (needsSellingTerms && !saving)) && (
          <div className="shrink-0 border-b border-amber-200 bg-amber-50 px-6 py-2 text-[12px]">
            {error ? (
              <span className="flex items-start gap-1.5 text-red-700">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                {error}
              </span>
            ) : saveBlockers.length > 0 ? (
              <span className="text-amber-800">
                To {isDraft ? 'publish' : 'save'}: {saveBlockers.join(', ')}.
                {canSaveDraft && ' You can Save draft now and finish later.'}
              </span>
            ) : (
              <span className="text-amber-800">
                {publishing
                  ? 'Sold to tenants, but it has no selling terms yet — so it cannot be published. Set the terms and publishing unlocks; until then it stays a draft.'
                  : 'Sold to tenants, but it has no selling terms yet — nobody can actually be sold it until the terms and the finance agreement exist.'}
              </span>
            )}
          </div>
        )}

        {loading ? (
          <RubyLoader variant="page" />
        ) : (
          <div className="flex-1 overflow-y-auto">
            {/* Screen 1 — Name + kind picker. Always first: the rest of the
                form only appears once a kind is chosen (new item) or was
                already resolved (existing item, defaults 'serialized'). */}
            {/* Name + kind, in the form's own column so it does not read as a
                separate band above the page. */}
            <div className="mx-auto w-full max-w-[1180px] px-6 pt-6">
              <section id="dq-name" className="dq-card p-4">
                <label className="mb-1 block text-xs font-semibold text-gray-700">Name</label>
                <input
                  value={form.modelName}
                  onChange={(e) => {
                    const name = e.target.value;
                    set('modelName', name);
                    // New item, ID untouched → the Device ID tracks the name.
                    if (!modelId && !idTouched) {
                      set('modelId', genModelId(name, new Set(allModels.map((m) => m.modelId))));
                    }
                  }}
                  placeholder="USB-C Cable 2 m"
                  className="mb-3 w-full rounded-lg border border-gray-300 bg-white text-stone-800 px-3 py-2 text-sm"
                />
                <label className="mb-1 block text-xs font-semibold text-gray-700">1 · What kind of equipment is this?</label>
                {kindLocked ? (
                  <div className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-600">
                    <Lock size={12} /> {KIND_COPY[form.trackingMode || 'serialized'].title} — locked, stock exists
                  </div>
                ) : (
                  <>
                    {/* A SEGMENTED CONTROL, not two billboards. It is one binary
                        choice and it was taking a third of the screen; the
                        explanation of whichever option is chosen sits under it,
                        which is the only part that changes. */}
                    <div className="dq-seg mt-1">
                      {(['serialized', 'quantity'] as const).map((k) => (
                        <button
                          key={k}
                          type="button"
                          onClick={() => set('trackingMode', k)}
                          className={form.trackingMode === k ? 'on' : ''}
                        >
                          {KIND_COPY[k].title}
                        </button>
                      ))}
                    </div>
                    {form.trackingMode && (
                      <p className="dq-hint mt-2">
                        <b style={{ color: 'var(--body)' }}>{KIND_COPY[form.trackingMode].title}</b>
                        {` — ${KIND_COPY[form.trackingMode].example}`}
                      </p>
                    )}
                  </>
                )}
              </section>

              {/* LIFECYCLE & SELLING sits with the NAME, not buried mid-form.
                  What state this device is in and whether it is sold at all
                  are the two facts every other answer on the page depends on,
                  and they were being answered pages apart. It waits for a kind
                  to be picked, like the rest of the form. */}
              {form.trackingMode && (
                <section id="dq-lifecycle" className="dq-card mt-4 p-4">
                  <div className="flex flex-col gap-3">
                    <div>
                      <h3 className="dq-sechead mb-2">Lifecycle</h3>
                      <StatusBar
                        /* Draft is never offered here — not even locked. It is
                           written by Save draft and cleared by Publish device in
                           the top bar, so the lifecycle starts at Pilot. A row
                           that IS a draft simply matches nothing, and says so. */
                        options={DEVICE_STATUS_OPTIONS}
                        value={form.lifecycle?.status || 'active'}
                        unmatchedHint="Saved as a draft, and kept out of bundles and deals. Pick the state it should go live in — or use Publish device in the top bar, which makes it Active."
                        onChange={(v) => {
                          set('lifecycle.status', v);
                          // Stamp the retirement date when it is actually retired, and
                          // clear it on the way back so a revived device is not left
                          // claiming an end-of-life date.
                          if (v === 'eol' || v === 'discontinued') {
                            if (!form.lifecycle?.eolDate) set('lifecycle.eolDate', Date.now());
                          } else {
                            set('lifecycle.eolDate', undefined);
                            set('lifecycle.replacedByModelId', '');
                          }
                        }}
                      />
                    </div>
                    <div>
                      {isRetired ? (
                        <>
                          <label className="mb-1.5 block text-xs font-semibold text-gray-700">
                            Replaced by <span className="text-red-600">*</span>
                          </label>
                          <div className={needsReplacement ? 'rounded-lg ring-2 ring-red-300' : ''}>
                            <SearchableDropdown
                              options={others
                                .filter((m) => m.category === form.category)
                                .map((m) => ({ value: m.modelId, label: m.modelName, subtitle: m.manufacturer }))}
                              value={form.lifecycle?.replacedByModelId || null}
                              onChange={(v) => set('lifecycle.replacedByModelId', v || '')}
                              placeholder="— choose a replacement —"
                              searchPlaceholder="Search devices..."
                            />
                          </div>
                          <p className="mt-2 text-[11px] text-gray-500">
                            Retiring a device must name its successor, so nobody is guessing six months from now.
                          </p>
                        </>
                      ) : null}
                    </div>

                    {/* SOLD TO TENANTS — the same question as the lifecycle: is
                        this thing offered, and in what state. Kept as its own
                        bordered block so the two answers stay legible, but in
                        one card so neither is answered without the other. */}
                    <div className="rounded-xl border-2 p-3.5" style={{ borderColor: '#fbcfe0', background: '#fffdfd' }}>
                      <h3 className="mb-1 text-xs font-bold uppercase tracking-wide" style={{ color: '#be123c' }}>
                        Is this sold to tenants?
                      </h3>
                      <p className="mb-3 text-[11px] leading-relaxed text-gray-600">
                        Stock and sellability are different questions — a cable is inventory and is never offered to a
                        tenant. Prices and terms are set in the financing wizard, not here.
                      </p>
                      <div className="flex items-start justify-between gap-4">
                        <p className="dq-hint flex-1">
                          {form.sellable === true
                            ? 'Continue to selling & financing terms after publishing: buy, finance or rent.'
                            : form.sellable === false
                              ? 'Cables, packaging, labels. It lands in stock and the process ends here — reversible later.'
                              : 'Pick one. Prices and terms are set in the financing wizard, not here.'}
                        </p>
                        <div className="dq-seg shrink-0">
                          <button
                            type="button"
                            onClick={() => { setSellableTouched(true); set('sellable', false); }}
                            className={form.sellable === false ? 'on' : ''}
                          >
                            Stock only
                          </button>
                          <button
                            type="button"
                            onClick={() => { setSellableTouched(true); set('sellable', true); }}
                            className={form.sellable === true ? 'on' : ''}
                          >
                            Sold to tenants
                          </button>
                        </div>
                      </div>
                      {modelId && typeof form.sellable !== 'boolean' && (
                        <p className="mt-2 text-[10.5px] text-gray-500">
                          Never answered for this device. It is treated as sold only while it carries offers — pick one to
                          settle it.
                        </p>
                      )}
                    </div>
                  </div>
                </section>
              )}
            </div>

            {!form.trackingMode ? (
              <p className="px-6 py-10 text-center text-xs text-gray-500">Pick a kind above to continue.</p>
            ) : (
            <>
            {/* ── ONE COLUMN.
                 The three-column grid split related questions across columns:
                 what kind of thing it is on the left, whether it sells on the
                 right, and the answer to one changed what the other meant. A
                 single column reads top to bottom in the order the decisions
                 are actually made. The three wrappers below survive as groups
                 so the conditionals inside them are untouched. */}
            <div className="mx-auto grid w-full max-w-[1180px] gap-5 p-6 lg:grid-cols-[176px_minmax(0,1fr)]">
              {/* ── SECTION RAIL. On a form this long, "what is left before I
                   can publish" should not require scrolling to find out. The
                   dots read from `saveBlockers`, the same list the footer and
                   the Publish button already use — no second idea of done. */}
              <aside className="hidden self-start lg:block lg:sticky lg:top-[72px]">
                <div className="mb-2 px-2 text-[11px] font-bold uppercase tracking-wider text-gray-400">Sections</div>
                <nav className="flex flex-col gap-0.5">
                  {([
                    /* Name & kind and Lifecycle sit above this rail rather than
                       beside it, but they are still sections of the form — a
                       rail that skipped them made the two cards an operator
                       scrolls back to most the only ones with no way back. */
                    { id: 'dq-name', label: 'Name & kind', todo: !form.modelName || !form.trackingMode },
                    { id: 'dq-lifecycle', label: 'Lifecycle & selling',
                      todo: !modelId && typeof form.sellable !== 'boolean' },
                    { id: 'dq-identity', label: 'Media & identity', todo: false },
                    { id: 'dq-technical', label: 'Screen type',
                      todo: !modelId && form.category === 'pos_terminal' && !form.appProfile?.terminalFormFactor },
                    { id: 'dq-technical', label: 'Integrated with', todo: false },
                    { id: 'dq-compat', label: 'Works with', todo: false },
                    { id: 'dq-documents', label: 'Documents', todo: false },
                  ] as const).map((row, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => document.getElementById(row.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                      className="flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-[12.5px] text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                    >
                      <span
                        className={`h-1.5 w-1.5 shrink-0 rounded-full ${row.todo ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      />
                      {row.label}
                    </button>
                  ))}
                </nav>
                {/* LEGEND — a key for the dots, not another section. It read as
                    one while it sat flush against the list and explained only
                    half the colours; ruled off and showing BOTH dots, it can
                    only be read as what it is. */}
                <div className="mt-3 border-t border-gray-200 px-2.5 pt-2.5">
                  {/* Two words, one line each, dots aligned with the rows above.
                      The longer wording wrapped into a ragged block in a 176px
                      rail — the sentence it was carrying belongs to the footer
                      message, which already says what is missing and why. */}
                  <div className="flex items-center gap-2.5 text-[11px] text-gray-500">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                    Incomplete
                  </div>
                  <div className="mt-1 flex items-center gap-2.5 text-[11px] text-gray-500">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                    Complete
                  </div>
                </div>
              </aside>

              <div className="flex min-w-0 flex-col gap-4">
            {/* Identity group */}
            <div id="dq-identity" />
            <div className="flex flex-col gap-4">
              <section className="dq-card p-4">
                <ImageGallery
                  photos={form.media?.photos || []}
                  heroImageUrl={form.media?.heroImageUrl}
                  videos={form.media?.videos || []}
                  subject={form.modelName}
                  modelId={form.modelId}
                  onChange={(next) => set('media', { ...(form.media || {}), ...next })}
                />
              </section>

              <section className="dq-card p-4">
                <h3 className="dq-sechead mb-3">Identity</h3>
                <label className="mb-1 block text-xs font-semibold text-gray-700">Device ID (SKU)</label>
                <div className="mb-1 flex items-center gap-1.5">
                  <input
                    value={form.modelId}
                    onChange={(e) => { setIdTouched(true); set('modelId', e.target.value.trim()); }}
                    disabled={!!modelId}
                    placeholder="auto-generated from the name"
                    className="w-full rounded-lg border border-gray-300 bg-white text-stone-800 px-3 py-2 font-mono text-sm disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  {!modelId && (
                    <button
                      type="button"
                      onClick={() => { setIdTouched(false); set('modelId', genModelId(form.modelName, new Set(allModels.map((m) => m.modelId)))); }}
                      title="Regenerate from the name"
                      className="shrink-0 rounded-lg border border-gray-300 p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                    >
                      <RefreshCw size={14} />
                    </button>
                  )}
                </div>
                <p className="mb-3 text-[10.5px] text-gray-400">
                  {modelId ? 'Locked once created.' : 'Minted from the name — edit it or regenerate if you want a different one.'}
                </p>
                <label className="mb-1 block text-xs font-semibold text-gray-700">Category</label>
                <div className="mb-3">
                  <SearchableDropdown
                    options={CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABEL[c] }))}
                    value={form.category}
                    onChange={(v) => {
                      const next = v || 'pos_terminal';
                      set('category', next);
                      // Follow the category until somebody answers by hand, and
                      // only on a NEW device — changing an existing device's
                      // category must not silently change whether it sells.
                      if (!modelId && !sellableTouched && next in SELLABLE_BY_DEFAULT) {
                        set('sellable', SELLABLE_BY_DEFAULT[next]);
                      }
                    }}
                    placeholder="Select category"
                    searchPlaceholder="Search categories..."
                  />
                </div>

                {/*
                  HOW THIS DEVICE TAKES A CARD — asked here, once, so that by the
                  time an order for it reaches the rail we already know which
                  fields to ask the operator for.

                  Only for payment devices; a printer has no lane. The two
                  Dejavoo configs are the live choices and NMI's two sit under
                  Legacy: NMI keeps working exactly as it does today and is
                  registered from the POS terminal screen, but it is never the
                  answer for a device added now.

                  A custom dropdown, never a native <select> — platform rule.
                */}
                {form.category === 'payment_device' && (() => {
                  const chosen = form.paymentConfigId
                    ? getPaymentConfig(form.paymentConfigId)
                    : undefined;
                  /* What an existing row resolves to today, when nobody has
                     used this picker on it yet. Shown rather than silently
                     pre-selected: a seeded profile is a fact about the row, an
                     answer is a decision, and the two must not blur. */
                  const inherited = !form.paymentConfigId
                    ? resolvePaymentConfig({
                        paymentProfile: form.paymentProfile,
                        modelName: form.modelName,
                      })
                    : undefined;
                  return (
                    <>
                      <label className="mb-1 block text-xs font-semibold text-gray-700">
                        Payment Device Config
                        {!modelId && <span className="ml-1 text-red-500">*</span>}
                      </label>
                      <div className="mb-1">
                        <SearchableDropdown
                          options={[
                            ...SELECTABLE_PAYMENT_CONFIGS.map((c) => ({
                              value: c.id, label: c.label, subtitle: c.hint,
                            })),
                            ...LEGACY_PAYMENT_CONFIGS.map((c) => ({
                              value: c.id, label: `${c.label} — legacy`, subtitle: c.hint,
                            })),
                          ]}
                          value={form.paymentConfigId || null}
                          onChange={(v) => {
                            set('paymentConfigId', v || undefined);
                            /*
                              `paymentProfile` STAYS WRITTEN, derived from the
                              choice. Every existing reader — the seed script,
                              the POI modal's subtitle, reporting — reads that
                              shape, and the point of this picker is to stop
                              guessing, not to strand them.

                              `none` clears it: there is no honest
                              connectionType/creditMethod/emvMode for a device
                              whose lane nobody has chosen, and inventing one is
                              how `cloud` became the silent default.
                            */
                            set('paymentProfile', getPaymentConfig(v || undefined)?.profile);
                          }}
                          placeholder={
                            inherited
                              ? `Currently reads as ${inherited.label} — confirm or change`
                              : 'Select how this device takes a card'
                          }
                          searchPlaceholder="Search payment configs..."
                        />
                      </div>
                      <p className="mb-3 text-[10.5px] leading-relaxed text-gray-400">
                        {chosen
                          ? chosen.hint
                          : inherited
                            ? `No config set. It resolves to ${inherited.label} from ${form.paymentProfile ? 'its stored profile' : 'its model name'} — pick one to make that explicit.`
                            : 'Decides what the order asks for when this device ships. Pick “No Payment Config (yet)” for a device in for testing.'}
                      </p>

                      {/*
                        CAN THIS MODEL ALSO GO THE OTHER WAY?

                        A Dejavoo P Series terminal is the same physical device
                        whether it is wired to the till or reached over SPIn's
                        cloud — the credentials are identical and only the route
                        differs. Which route a given unit takes is decided at
                        REGISTRATION, by whoever has the terminal in hand; the
                        catalog cannot know, because the unit has not been to a
                        store yet.

                        So this ticks the OTHER lane as available, and the order
                        screen shows a picker instead of assuming. Left unticked
                        the model behaves exactly as it always has, with no
                        picker anywhere — which is every row written before this
                        existed.

                        Offered only for the P-series routes (SPIn USB, SPIn
                        Cloud, RubyTill Pay). An NMI device has no second
                        route, and a Dejavoo Online processor has no terminal
                        at all. One switch per OTHER route; a model with none
                        on behaves exactly as it always has.
                      */}
                      {(P_SERIES_LANES as readonly string[]).includes(form.paymentConfigId || '') && (() => {
                        const primary = form.paymentConfigId as string;
                        const stored: string[] = (form.paymentConfigOptions || []) as string[];
                        const others = P_SERIES_LANES.filter((l) => l !== primary);
                        const extras = others.filter((l) => stored.includes(l));
                        const toggle = (lane: string, v: boolean) => {
                          const next = v ? [...extras, lane] : extras.filter((l) => l !== lane);
                          /* OFF sends `[]`, never `undefined`. The model
                             upsert merges `{...existing, ...body}` and
                             JSON.stringify drops an undefined-valued key,
                             so `undefined` would leave the old list
                             standing and the toggle would not turn off. An
                             empty array survives the round trip, and
                             `paymentConfigOptionsFor` already reads it as
                             "only the resolved config". The primary is
                             always listed first when anything is on. */
                          set(
                            'paymentConfigOptions',
                            (next.length
                              ? [primary, ...P_SERIES_LANES.filter((l) => next.includes(l))]
                              : []) as any,
                          );
                        };
                        return (
                          <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1">
                            {others.map((lane) => {
                              const cfg = getPaymentConfig(lane);
                              return (
                                <div key={lane} className="flex items-start gap-2.5 py-1.5" data-testid={`lane-option-${lane}`}>
                                  <Toggle on={extras.includes(lane)} onChange={(v) => toggle(lane, v)} />
                                  <div className="min-w-0">
                                    <p className="text-[12px] font-semibold text-gray-800">
                                      Can also be set up as “{cfg?.label || lane}”
                                    </p>
                                    {lane === 'dejavoo_rubytill_pay' && (
                                      <p className="mt-0.5 text-[10.5px] leading-relaxed text-gray-500">
                                        Our own payment app on the terminal; pairs with a code, needs only the TPN.
                                      </p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                            <p className="pb-1.5 text-[10.5px] leading-relaxed text-gray-500">
                              The same terminal; only the route differs. Turn one on and whoever registers the
                              device picks the route, defaulting to the config above.
                            </p>
                          </div>
                        );
                      })()}
                    </>
                  );
                })()}

                <label className="mb-1 block text-xs font-semibold text-gray-700">Brand</label>
                <div className="mb-3">
                  <SearchableDropdown
                    options={[...new Set([...brandList, ...(form.brand ? [form.brand] : [])])].map((b) => ({ value: b, label: b }))}
                    value={form.brand || null}
                    onChange={(v) => set('brand', v || '')}
                    onCreateNew={(name) => set('brand', name)}
                    createLabel="Add brand"
                    placeholder="— pick from the Brands list —"
                    searchPlaceholder="Search brands..."
                  />
                </div>
                {/*
                  ONLY BULK STOCK HAS A BARCODE AT THE MODEL.

                  `sharedBarcode` is, per its schema note, "the ONE barcode
                  every unit of a 'quantity' model shares" — and it is
                  MEANINGLESS for a serialized one. It used to be drawn for
                  both kinds, which told an operator creating a Dejavoo P17
                  that every P17 in the building answers to one code. They do
                  not: a serialized unit gets its OWN `RTA-######` minted at
                  GRN receive, and that is the code printed on the sticker
                  that goes on the box (see `ReceivedUnitCard`).

                  So the barcode question is asked of bulk items only. Nothing
                  is deleted — a model that already carries one keeps it, and
                  `modelSearch` still matches on it so scanning a carton of
                  cables still finds the line.
                */}
                {isQuantity && (
                  <>
                    <label className="mb-1 block text-xs font-semibold text-gray-700">
                      Internal barcode <span className="font-normal text-gray-400">(Ruby Till)</span>
                    </label>
                    <div className="mb-1 flex items-center gap-1.5">
                      <input value={form.sharedBarcode || ''} onChange={(e) => set('sharedBarcode', e.target.value)}
                        placeholder="RT0000000000"
                        className="w-full rounded-lg border border-gray-300 bg-white text-stone-800 px-3 py-2 font-mono text-sm" />
                      <button
                        type="button"
                        onClick={() => set('sharedBarcode', genInternalBarcode())}
                        title="Mint a new internal barcode"
                        className="shrink-0 rounded-lg border border-gray-300 p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                      >
                        <RefreshCw size={14} />
                      </button>
                    </div>
                    <p className="mb-3 text-[10.5px] text-gray-400">
                      The one barcode all stock of this item shares.
                    </p>
                  </>
                )}
                {!isQuantity && (
                  <p className="mb-3 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-[10.5px] leading-relaxed text-stone-500">
                    Counted individually, so there is no barcode here — each unit gets its own
                    <span className="font-mono"> RTA-###### </span>
                    when it is received against a GRN, and that is the label printed for it.
                  </p>
                )}
                {isQuantity ? (
                  <>
                    <label className="mb-1 block text-xs font-semibold text-gray-700">Reorder point</label>
                    <input
                      type="number" min="0"
                      value={form.reorderPoint ?? ''}
                      onChange={(e) => set('reorderPoint', e.target.value === '' ? undefined : Number(e.target.value))}
                      placeholder="150"
                      className="w-full rounded-lg border border-gray-300 bg-white text-stone-800 px-3 py-2 text-sm"
                    />
                  </>
                ) : (
                  <>
                    <label className="mb-1 block text-xs font-semibold text-gray-700">
                      Manufacturer <span className="font-normal text-gray-400">(defaults to brand)</span>
                    </label>
                    <input value={form.manufacturer} onChange={(e) => set('manufacturer', e.target.value)}
                      placeholder={form.brand || ''}
                      className="mb-3 w-full rounded-lg border border-gray-300 bg-white text-stone-800 px-3 py-2 text-sm" />
                    <label className="mb-1 block text-xs font-semibold text-gray-700">Description</label>
                    <textarea value={form.description || ''} onChange={(e) => set('description', e.target.value)} rows={2}
                      className="w-full rounded-lg border border-gray-300 bg-white text-stone-800 px-3 py-2 text-sm" />
                  </>
                )}
              </section>


              <section id="dq-documents" className="dq-card p-4">
                <DocumentsPanel
                  documents={form.documents || []}
                  modelId={form.modelId}
                  onChange={(next) => set('documents', next)}
                />
              </section>

              <details className="dq-card" open>
                <summary className="flex cursor-pointer list-none items-center gap-2.5 p-4">
                  <ChevronRight size={13} className="dq-chev shrink-0 text-gray-400" />
                  <span className="dq-sechead">Cost</span>
                  <span className="text-[11px] text-gray-400">Unit, shipping, duty — the landed figure</span>
                </summary>
                <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                <h3 className="dq-sechead mb-3">
                  Economics · what it costs us
                </h3>
                {/* Full economics for BOTH kinds — a bulk cable's landed cost
                    matters exactly as much as a terminal's. */}
                {([
                  ['unitCostCents', 'Unit cost'],
                  ['inboundShippingCents', 'Inbound shipping'],
                  ['dutyCents', 'Duty / customs'],
                ] as const).map(([k, label]) => (
                  <div key={k} className="mb-2 flex items-center justify-between gap-3">
                    <label className="text-sm text-gray-600">{label}</label>
                    <div className="flex items-center gap-1">
                      <span className="text-sm text-gray-400">$</span>
                      <input
                        type="number" step="0.01" min="0"
                        value={centsToInput(form.economics?.[k])}
                        onChange={(e) => set(`economics.${k}`, inputToCents(e.target.value))}
                        className="w-28 rounded-lg border border-gray-300 bg-white text-stone-800 px-2 py-1.5 text-right font-mono text-sm"
                      />
                    </div>
                  </div>
                ))}
                <div className="mt-3 flex items-center justify-between border-t border-gray-200 pt-3">
                  <span className="text-sm font-bold text-gray-900">Landed cost</span>
                  <span className="font-mono text-base font-bold text-gray-900">{money(landed)}</span>
                </div>
                <p className="mb-3 text-[11px] text-gray-500">Computed — not editable.</p>
                <div className="flex items-center justify-between gap-3 border-t border-dashed border-gray-200 pt-3">
                  <label className="text-sm text-gray-600">
                    Outbound shipping <span className="text-[10px] text-gray-400">(default est.)</span>
                  </label>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-gray-400">$</span>
                    <input
                      type="number" step="0.01" min="0"
                      value={centsToInput(form.economics?.defaultOutboundShippingCents)}
                      onChange={(e) => set('economics.defaultOutboundShippingCents', inputToCents(e.target.value))}
                      className="w-28 rounded-lg border border-gray-300 bg-white text-stone-800 px-2 py-1.5 text-right font-mono text-sm"
                    />
                  </div>
                </div>
                </div>
              </details>
            </div>

            {/* MIDDLE + RIGHT — specs/vendor/app-profile/compatibility/offers/usage.
                Rendered for BOTH kinds: supported devices, vendor and economics
                matter whether stock is tracked individually or in bulk. Only the
                specs + app-profile sections stay serialized-only — a wire has no
                Android version or form factor. */}
            {/* Technical group */}
            <div id="dq-technical" />
            <div className="flex flex-col gap-4">
              {/* Specs + app profile are serialized-only — a wire has no Android
                  version or form factor. Vendor/compatibility below render for
                  BOTH kinds. */}
              {!isQuantity && (
              <>
              <details className="dq-card" open>
                <summary className="flex cursor-pointer list-none items-center gap-2.5 p-4">
                  <ChevronRight size={13} className="dq-chev shrink-0 text-gray-400" />
                  <span className="dq-sechead">Specs</span>
                  <span className="text-[11px] text-gray-400">Screen, printer and port detail</span>
                </summary>
                <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                <h3 className="dq-sechead mb-1">Technical details</h3>
                <p className="mb-3 text-[11px] text-gray-500">Leave blank what does not apply — a cable has no Android version.</p>
                {([
                  ['androidVersion', 'Android version', 'text'],
                  ['soc', 'SoC', 'text'],
                  ['ramGb', 'RAM (GB)', 'number'],
                  ['storageGb', 'Storage (GB)', 'number'],
                ] as const).map(([k, label, type]) => (
                  <div key={k} className="mb-2">
                    <label className="mb-1 block text-xs font-semibold text-gray-700">{label}</label>
                    <input
                      type={type}
                      value={form.specs?.[k] ?? ''}
                      onChange={(e) => set(`specs.${k}`, type === 'number' ? (e.target.value === '' ? undefined : Number(e.target.value)) : e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white text-stone-800 px-3 py-2 text-sm"
                    />
                  </div>
                ))}
                <div className="mb-2">
                  <label className="mb-1 block text-xs font-semibold text-gray-700">Screen resolution</label>
                  <input value={form.specs?.screen?.resolution ?? ''}
                    onChange={(e) => set('specs.screen', { ...(form.specs?.screen || {}), resolution: e.target.value })}
                    placeholder="1920x1080" className="w-full rounded-lg border border-gray-300 bg-white text-stone-800 px-3 py-2 text-sm" />
                </div>
                <div className="mb-2">
                  <label className="mb-1 block text-xs font-semibold text-gray-700">Screen panel</label>
                  <input value={form.specs?.screen?.panel ?? ''}
                    onChange={(e) => set('specs.screen', { ...(form.specs?.screen || {}), panel: e.target.value })}
                    placeholder="IPS" className="w-full rounded-lg border border-gray-300 bg-white text-stone-800 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-700">Printer USB id</label>
                  <input value={form.specs?.printer?.usbId ?? ''}
                    onChange={(e) => set('specs.printer', { ...(form.specs?.printer || {}), usbId: e.target.value, builtIn: true })}
                    placeholder="0fe6:811e" className="w-full rounded-lg border border-gray-300 bg-white text-stone-800 px-3 py-2 font-mono text-sm" />
                </div>
                </div>
              </details>

              {/* SUNMI card setup (sunmi-card-setup.md, board 5) — each renders
                  nothing unless the row is a payment device / carries a policy. */}
              <ReaderCapabilitiesPanel form={form} setForm={setForm} />
              <CardSetupPolicyPanel
                form={form}
                allModels={allModels as any}
                setForm={setForm}
                buildLabel={buildTargetChoices.find((c) => c.value === form.buildTarget)?.label || null}
              />

              <section className="dq-card p-4">
                <h3 className="dq-sechead mb-3">Screen type</h3>
                {/* `appProfile.deviceType` USED TO BE ASKED FOR HERE, and it
                    was a dead field: nothing in the platform ever read it.
                    Branding does not consult the hardware catalog at all —
                    device-config-service's `listDevices` carries a hardcoded
                    `DEVICE_TYPES = ['android_dual_screen']` and the Branding tab
                    defaults to that same literal. So the input collected a value
                    no code consumed, under help text promising a link to
                    Branding & Fleet that did not exist.
                    The SCHEMA field stays (rows that already carry one must keep
                    parsing — nothing is removed from stored data); it is simply
                    no longer asked for. If branding is ever driven from the
                    catalog, this is where the question comes back. */}
                {/* FORM FACTOR IS A POS-TERMINAL QUESTION.
                    It exists to set a terminal's `terminalType` at registration,
                    and a cable, a poster or a screwdriver never registers as a
                    terminal. Asking for it on those invites an answer that is
                    then carried around meaning nothing. Existing non-terminal
                    devices that already carry one still show it, so nothing an
                    operator entered is hidden from them. */}
                {(form.category === 'pos_terminal' || form.appProfile?.terminalFormFactor) && (
                <>
                <label className="mb-1 block text-xs font-semibold text-gray-700">Screen type <span className="text-red-500">*</span></label>
                <div className="mb-2">
                  {/* CLOSED LIST, AS A BOARD.
                      Six tiles of identical size rather than a dropdown: the
                      choice is short, fixed, and sits directly above the
                      "Integrated with" board — a dropdown next to a board reads
                      as two unrelated controls. RUBY, not green, because this is
                      one-of-six and that one is any-of-eleven; same shape must
                      not mean two different rules.
                      An invented value provisions as a plain `pos` terminal with
                      no warning, so a LEGACY value already stored here is shown
                      as its own tile, flagged, and can be replaced. */}
                  <div className="dq-board">
                    {SELECTABLE_FORM_FACTORS.map((ff) => {
                      const on = form.appProfile?.terminalFormFactor === ff;
                      const FIcon = SCREEN_TYPE_ICON[ff] || Monitor;
                      return (
                        <button
                          key={ff}
                          type="button"
                          role="radio"
                          aria-checked={on}
                          onClick={() => pickScreenType(ff)}
                          title={ff}
                          className={`dq-sw${on ? ' pick' : ''}`}
                        >
                          <span className="dq-sw-ico"><FIcon size={14} /></span>
                          <span className="dq-sw-nm">{formFactorLabel(ff)}</span>
                        </button>
                      );
                    })}
                    {/* A SUPERSEDED ANSWER IS STILL THIS DEVICE'S ANSWER.
                        'pos' and 'kiosk' are no longer offered — the first says
                        nothing `android_single_screen` does not say better, the
                        second cannot say how many screens the machine has — but
                        they remain VALID and models carry them. Drawing the
                        stored one as its own selected tile keeps the card
                        honest: without it the device would look unanswered and
                        an operator would "fix" it by picking something else. */}
                    {form.appProfile?.terminalFormFactor
                      && FORM_FACTORS.includes(form.appProfile.terminalFormFactor as any)
                      && !SELECTABLE_FORM_FACTORS.includes(form.appProfile.terminalFormFactor as any) && (
                      <button
                        type="button"
                        role="radio"
                        aria-checked
                        className="dq-sw pick"
                        title={`${form.appProfile.terminalFormFactor} — superseded, still valid`}
                      >
                        <span className="dq-sw-ico">
                          {React.createElement(
                            SCREEN_TYPE_ICON[form.appProfile.terminalFormFactor] || Monitor,
                            { size: 14 },
                          )}
                        </span>
                        <span className="dq-sw-nm">
                          {formFactorLabel(form.appProfile.terminalFormFactor)}
                          <span className="block text-[10px] opacity-70">superseded</span>
                        </span>
                      </button>
                    )}
                    {form.appProfile?.terminalFormFactor
                      && !FORM_FACTORS.includes(form.appProfile.terminalFormFactor as any) && (
                      <button
                        type="button"
                        className="dq-sw"
                        style={{ borderColor: '#FCD34D', background: '#FFFBEB' }}
                        title="Not a recognised type — pick a replacement"
                      >
                        <span className="dq-sw-ico" style={{ background: '#FEF3C7', color: '#B45309' }}>
                          <AlertTriangle size={14} />
                        </span>
                        <span className="dq-sw-nm" style={{ color: '#92400E' }}>
                          {form.appProfile.terminalFormFactor}
                          <span className="block text-[10px]">not recognised</span>
                        </span>
                      </button>
                    )}
                  </div>
                </div>
                {/* AN INVENTED FORM FACTOR SILENTLY BECOMES A PLAIN TERMINAL.
                    super-admin-ext2's provisioning checks `validTypes` and falls
                    back to 'pos' for anything else — no error, no warning, and
                    the device loses its customer-facing screen behaviour
                    (`CustomerFacingTab` matches 'android_dual_screen' exactly).
                    The field stays free-form so stored rows keep parsing, but a
                    value provisioning will not honour has to SAY so here, where
                    it can still be changed. */}
                {form.appProfile?.terminalFormFactor
                  && !FORM_FACTORS.includes(form.appProfile.terminalFormFactor as any) && (
                  <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" />
                    <p className="text-[11px] leading-relaxed text-amber-800">
                      <b>&ldquo;{form.appProfile.terminalFormFactor}&rdquo; is not a recognised
                      screen type.</b> A terminal registered from this device is silently
                      recorded as a plain <code>pos</code> terminal and never gets
                      customer-facing screen behaviour. Pick a replacement above — for a
                      dual-screen device that is <b>Android Dual Screen POS Terminal</b>.
                    </p>
                  </div>
                )}
                <p className="mb-3 text-[11px] text-gray-500">
                  Sets a terminal&rsquo;s type automatically when this device is registered on
                  Devices → Terminals. Choose <b>Not Applicable</b> for a POS-category item that
                  is never registered as a terminal.
                </p>

                {/* BUILD — WHICH APK EVERY UNIT OF THIS MODEL INSTALLS.
                    NOT derived from the screen type, and an earlier attempt to
                    derive it was wrong: a K10 and a Touch Dynamic Vector are
                    both dual-screen Android terminals and take DIFFERENT
                    builds, because the K10 carries built-in peripherals whose
                    vendor SDKs the Vector has no use for. Screen type decides
                    how a terminal PROVISIONS; this decides what is installed on
                    it. Two questions, and neither answers the other.
                    The list is the one the installer validates against, so a
                    build cannot be chosen here that `install-device.sh` would
                    then reject as an unknown target. */}
                {buildTargetApplies && (
                <div className="mb-3">
                  <label className="mb-1 block text-xs font-semibold text-gray-700">Build</label>
                  <SearchableDropdown
                    value={form.buildTarget || ''}
                    onChange={(v) => set('buildTarget', v || '')}
                    options={buildTargetChoices}
                    placeholder="Select the build this device installs"
                  />
                  {/* SUPPORTED DEVICES — the question an operator actually has
                      in front of a new box: "is my machine covered by this?".
                      Descriptive, NOT a gate: nothing refuses an install
                      because a device is missing from this list, and saying so
                      matters because a list that looks like a whitelist gets
                      treated as one. What is actually checked is the model's
                      Hardware IDs, below. */}
                  {buildDevices.length > 0 && (
                    <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                      <p className="text-[10.5px] font-semibold uppercase tracking-wide text-gray-500">
                        Supported devices
                      </p>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {buildDevices.map((dv) => (
                          <span
                            key={dv}
                            className="rounded border border-gray-200 bg-white px-1.5 py-0.5 font-mono text-[10.5px] text-gray-700"
                          >
                            {dv}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="mt-1 text-[11px] text-gray-500">
                    Every unit of this model installs this. The install screen reads it from
                    here instead of asking each time.
                  </p>
                  <p className="mt-1 text-[11px] text-gray-500">
                    Also sets the live update lane for every till of this model already
                    installed — a lane with no release promoted means those tills stop
                    receiving updates.
                  </p>
                </div>
                )}

                {/* CAPABILITIES + HARDWARE PROFILE — the catalog model's device
                    profile block. Backend already reads `appProfile.capabilities`
                    and `appProfile.installProfileId`; this is where an operator
                    sets them. The install screen (DeviceInstallPanel) reads
                    `installProfileId` to pre-select the hardware profile.
                    Only on devices that run the POS app (same gate as Build) —
                    a cash drawer, cable or pin pad has no till profile. An
                    existing answer still shows, so nothing entered is hidden. */}
                {(buildTargetApplies
                  || Object.keys(form.appProfile?.capabilities || {}).length > 0
                  || !!form.appProfile?.installProfileId) && (
                <div className="mb-3">
                  <CapabilitiesFields
                    capabilities={form.appProfile?.capabilities || {}}
                    installProfileId={form.appProfile?.installProfileId}
                    buildTarget={form.buildTarget || undefined}
                    onChange={({ capabilities, installProfileId }) =>
                      setForm((f: any) => ({ ...f, appProfile: { ...(f.appProfile || {}), capabilities, installProfileId } }))}
                  />
                </div>
                )}

                {/* WHAT THE DEVICE CALLS ITSELF.
                    Android `ro.product.model` — `TC-TOUCH-K10` on a K10. NOT a
                    display name and never typed from memory: it is read off the
                    device, and its whole value is that nobody chose it, so it
                    is the one identifier on a check-in that can contradict all
                    the others. A unit reporting a model no catalog row claims
                    is flagged on the Fleet tab.

                    A LIST because one model ships across firmware revisions
                    that report slightly different strings, and a single value
                    would make every such unit read as the wrong device. */}
                {posAppApplies && (
                <div className="mb-3">
                  <label className="mb-1 block text-xs font-semibold text-gray-700">
                    Hardware IDs
                  </label>
                  <input
                    value={hardwareIdsText}
                    onChange={(e) => setHardwareIdsText(e.target.value)}
                    onBlur={() => set('appProfile.hardwareIds', parseHardwareIds(hardwareIdsText))}
                    placeholder="TC-TOUCH-K10"
                    className="w-full min-h-[44px] rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-emerald-500"
                  />
                  <p className="mt-1 text-[11px] text-gray-500">
                    Read off the device, never typed from memory &mdash;{' '}
                    <code className="text-[10px]">adb shell getprop ro.product.model</code>.
                    Separate several with commas when one model ships across firmware
                    revisions. Leave blank and nothing is checked.
                  </p>
                </div>
                )}

                {/* BOOT ANIMATION — a property of the OS IMAGE, not of the app.
                    `DeviceBrandingPlugin` installs one by writing
                    /oem/media/bootanimation.zip AS ROOT. That works on an AOSP
                    terminal like the K10 and cannot work on a Google-certified
                    (GMS) device: verified boot, no `su`, no /oem. The install
                    already fails safe there — but without this answer the device
                    sits in the fleet as a permanently unbranded terminal and
                    somebody chases it every week. */}
                {posAppApplies && (
                <div className="mb-1 flex items-start justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2">
                  <div>
                    <p className="text-xs font-semibold text-gray-700">Custom boot animation</p>
                    <p className="mt-0.5 text-[11px] text-gray-500">
                      Off for a Google-certified (GMS) device &mdash; installing one needs root,
                      which those do not have. The fleet then stops reporting it as unbranded.
                    </p>
                  </div>
                  {/* `!== false`, not `=== true`: absent means UNANSWERED, and
                      every model predates this field. Rendering unanswered as
                      "off" would silently mark every K10 in the catalog as
                      unbrandable. */}
                  <Toggle
                    on={form.appProfile?.supportsBootAnimation !== false}
                    onChange={(v: boolean) => set('appProfile.supportsBootAnimation', v)}
                  />
                </div>
                )}
                </>
                )}

              </section>

              {/* INTEGRATED WITH — what the device has built in.
                  Shown for EVERY category, not just POS: a kiosk, a scale and a
                  printer all have USB, WiFi or a camera, and hiding the question
                  on those would make the answer absent rather than false.
                  Codes are stored; labels are display only, so wording can
                  change without touching a single stored row. */}
              <section className="dq-card p-4">
                <h3 className="dq-sechead mb-1">
                  Integrated with
                </h3>
                <p className="mb-3 text-[11px] text-gray-500">
                  What is built into this device. Off means it does not have it — not unknown.
                </p>
                <div className="dq-board">
                  {INTEGRATIONS.map(({ code, label }) => {
                    const IIcon = INTEGRATION_ICON[code] || Package;
                    const on = (form.integrations || []).includes(code);
                    return (
                      <button
                        key={code}
                        type="button"
                        role="switch"
                        aria-checked={on}
                        onClick={() => set(
                          'integrations',
                          on
                            ? (form.integrations || []).filter((c: string) => c !== code)
                            : [...(form.integrations || []), code],
                        )}
                        className={`dq-sw${on ? ' on' : ''}`}
                      >
                        <span className="dq-sw-ico"><IIcon size={14} /></span>
                        <span className="dq-sw-nm">{label}</span>
                        {/* Track and knob, so the state reads at a glance and
                            not only from colour. */}
                        <span className="dq-sw-tr"><span className="dq-sw-kn" /></span>
                      </button>
                    );
                  })}
                </div>
              </section>
              </>
              )}

            </div>

            {/* Compatibility &amp; commercial group */}
            <div id="dq-compat" />
            <div className="flex flex-col gap-4">
              <section className="dq-card p-4">
                <div className="mb-1 flex items-center justify-between">
                  <h3 className="dq-sechead">Works with</h3>
                  <span className="text-[11px] text-gray-400">
                    <b className="text-gray-700">{(form.compatibility?.worksWith || []).length}</b>
                    {` of ${attachable.length} selected`}
                  </span>
                </div>
                <p className="mb-2.5 text-[11px] text-gray-500">
                  What can plug into this. Drives the picker when a bundle is built.
                </p>
                {form.category !== 'pos_terminal' ? (
                  <p className="rounded-lg bg-gray-50 p-3 text-[11px] text-gray-500">
                    Compatibility is declared on the host POS device. Open the terminal this attaches to and tick it there.
                  </p>
                ) : attachable.length === 0 ? (
                  <p className="rounded-lg bg-gray-50 p-3 text-[11px] text-gray-500">No other devices in the catalog yet.</p>
                ) : (
                  <>
                    {/* SEARCH + FILTER, because this list only grows. Grouping by
                        category alone stopped working somewhere past twenty
                        devices: the group you wanted was always off-screen. */}
                    <div className="mb-2 flex gap-2">
                      <div className="relative flex-1">
                        <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
                        <input
                          value={compatQuery}
                          onChange={(e) => setCompatQuery(e.target.value)}
                          placeholder="Search by name, brand or category…"
                          className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-8 pr-2 text-[12.5px] text-stone-800"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setCompatOnlyPicked((v) => !v)}
                        className={`shrink-0 rounded-lg border px-2.5 text-[11.5px] font-semibold ${
                          compatOnlyPicked
                            ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                            : 'border-gray-300 bg-white text-gray-600'
                        }`}
                      >
                        Selected
                      </button>
                    </div>

                    {/* CATEGORY PILLS — one click to "just the printers",
                        which is how an operator thinks about this list. The
                        row scrolls sideways rather than wrapping, so adding a
                        category never pushes the device list down the page. */}
                    <div className="mb-2 -mx-0.5 flex gap-1.5 overflow-x-auto px-0.5 pb-1">
                      {[{ value: '', label: 'All', count: attachable.length }, ...compatCategoryPills].map((p) => {
                        const on = compatCategory === p.value;
                        return (
                          <button
                            key={p.value || 'all'}
                            type="button"
                            onClick={() => setCompatCategory(p.value)}
                            className={`shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-[11.5px] font-semibold transition ${
                              on
                                ? 'border-emerald-600 bg-emerald-600 text-white'
                                : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                            }`}
                          >
                            {p.label}
                            <span className={`ml-1.5 ${on ? 'text-emerald-100' : 'text-gray-400'}`}>{p.count}</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* TWO COLUMNS — roughly 24 rows visible before scrolling,
                        instead of the handful a single column fitted. */}
                    <div className="grid max-h-[430px] grid-cols-1 gap-1 overflow-y-auto rounded-lg border border-gray-200 p-1.5 lg:grid-cols-2">
                      {visibleAttachable.length === 0 ? (
                        <p className="col-span-full p-3 text-center text-[11.5px] text-gray-400">
                          Nothing matches that search or filter.
                        </p>
                      ) : visibleAttachable.map((m) => {
                        const on = (form.compatibility?.worksWith || []).includes(m.modelId);
                        const RowIcon = CATEGORY_ICON[m.category] || Package;
                        return (
                          <label
                            key={m.modelId}
                            className={`flex cursor-pointer items-center gap-2 rounded-lg border p-1.5 transition ${
                              on ? 'border-emerald-200 bg-emerald-50' : 'border-transparent hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() => toggleWorksWith(m.modelId)}
                              className="accent-emerald-600"
                            />
                            {/* The PICTURE, so a rack of near-identical model
                                numbers is distinguishable at a glance. */}
                            <span className="grid h-8 w-8 flex-none place-items-center overflow-hidden rounded-md bg-gray-100">
                              {m.media?.heroImageUrl
                                ? <img src={m.media.heroImageUrl} alt="" className="h-full w-full object-cover" />
                                : <RowIcon size={14} className="text-gray-400" />}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-[12.5px] font-semibold text-gray-800">{m.modelName}</span>
                              <span className="block truncate text-[10px] text-gray-400">
                                {[m.brand, CATEGORY_LABEL[m.category], money(m.landedCostCents)].filter(Boolean).join(' · ')}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </section>

              <details className="dq-card" open>
                <summary className="flex cursor-pointer list-none items-center gap-2.5 p-4">
                  <ChevronRight size={13} className="dq-chev shrink-0 text-gray-400" />
                  <span className="dq-sechead">Vendor</span>
                  <span className="text-[11px] text-gray-400">Who supplies it, and on what terms</span>
                </summary>
                <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                <h3 className="dq-sechead mb-3">Vendor</h3>
                <label className="mb-1 block text-xs font-semibold text-gray-700">Supplier</label>
                <div className="mb-1">
                  {/* Picked from the Vendors section, never typed free-hand —
                      one vendor registry. A legacy free-text supplier already
                      on the model stays selectable so old rows don't blank. */}
                  <SearchableDropdown
                    options={[
                      ...vendorList.map((v) => ({ value: v.name, label: v.name })),
                      ...(form.vendor?.supplierName && !vendorList.some((v) => v.name === form.vendor.supplierName)
                        ? [{ value: form.vendor.supplierName, label: form.vendor.supplierName, subtitle: 'not in the vendor registry' }]
                        : []),
                    ]}
                    value={form.vendor?.supplierName || null}
                    onChange={(v) => {
                      set('vendor.supplierName', v || '');
                      // Convenience: seed lead time from the vendor record when
                      // the model doesn't have one yet.
                      const picked = vendorList.find((x) => x.name === v);
                      if (picked?.leadTimeDays && !form.vendor?.leadTimeDays) set('vendor.leadTimeDays', picked.leadTimeDays);
                    }}
                    onCreateNew={(name) => setNewVendor(name)}
                    createLabel="Add vendor"
                    placeholder="— select a vendor —"
                    searchPlaceholder="Search vendors..."
                  />
                </div>
                <p className="mb-3 text-[10.5px] text-gray-400">
                  Type a name and choose <b>Add vendor</b> to create one without leaving this form —
                  it is written to the vendor registry, the same record Inventory → Vendors edits.
                </p>
                <label className="mb-1 block text-xs font-semibold text-gray-700">Supplier SKU</label>
                <input value={form.vendor?.supplierSku || ''} onChange={(e) => set('vendor.supplierSku', e.target.value)}
                  className="mb-3 w-full rounded-lg border border-gray-300 bg-white text-stone-800 px-3 py-2 font-mono text-sm" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-700">Lead time (days)</label>
                    <input type="number" min="0" value={form.vendor?.leadTimeDays ?? ''}
                      onChange={(e) => set('vendor.leadTimeDays', e.target.value === '' ? undefined : Number(e.target.value))}
                      className="w-full rounded-lg border border-gray-300 bg-white text-stone-800 px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-700">MOQ</label>
                    <input type="number" min="0" value={form.vendor?.moq ?? ''}
                      onChange={(e) => set('vendor.moq', e.target.value === '' ? undefined : Number(e.target.value))}
                      className="w-full rounded-lg border border-gray-300 bg-white text-stone-800 px-3 py-2 text-sm" />
                  </div>
                </div>
                </div>
              </details>

              {/* THE LAST QUESTION (2026-08-30) — and the only one that decides
                  whether this flow continues. It replaces the old "Offers" note,
                  which said where selling terms live and then stopped there, and
                  it replaces the financing wizard's per-offer "Available in the
                  marketplace" checkbox: asked once, about the device, at the
                  moment it is actually being decided. */}

              {modelId && (
                <section className="dq-card p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="dq-sechead">Where it is used</h3>
                    {usage && (
                      <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${
                        usage.referenceCount === null ? 'bg-gray-200 text-gray-600'
                          : usage.idle ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {usage.referenceCount === null ? 'UNKNOWN'
                          : usage.idle ? 'IDLE' : `IN USE · ${usage.referenceCount}`}
                      </span>
                    )}
                  </div>

                  {!usage ? (
                    <RubyLoader variant="inline" size={14} label="Checking references…" />
                  ) : (
                    <>
                      {usage.references.bundles.length > 0 && (
                        <div className="mb-2">
                          <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Bundles</div>
                          {usage.references.bundles.map((b) => (
                            <div key={b.bundleId} className="flex items-center gap-1.5 py-0.5 text-xs text-gray-700">
                              <Link2 size={11} className="text-gray-400" />{b.name}
                            </div>
                          ))}
                        </div>
                      )}
                      {usage.references.skus.length > 0 && (
                        <div className="mb-2">
                          <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Priced by</div>
                          {usage.references.skus.map((sk) => (
                            <div key={sk.skuId} className="flex items-center gap-1.5 py-0.5 text-xs text-gray-700">
                              <Link2 size={11} className="text-gray-400" />
                              <span className="truncate">{sk.name}</span>
                              <span className="text-[10px] text-gray-400">({sk.via})</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {usage.references.replacementFor.length > 0 && (
                        <div className="mb-2">
                          <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Replaces</div>
                          {usage.references.replacementFor.map((r) => (
                            <div key={r.modelId} className="py-0.5 text-xs text-gray-700">{r.modelName}</div>
                          ))}
                        </div>
                      )}
                      <div className="text-xs text-gray-700">
                        Terminals:{' '}
                        {usage.references.terminals === null
                          ? <span className="font-semibold text-amber-700">could not check</span>
                          : <strong>{usage.references.terminals}</strong>}
                      </div>
                      <p className="mt-2 rounded-lg bg-gray-50 p-2 text-[11px] leading-relaxed text-gray-600">
                        {usage.reason}
                      </p>
                    </>
                  )}
                </section>
              )}
            </div>
            </div>
            </div>
            </>
            )}
          </div>
        )}

        {confirmDelete && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-black/50 p-6">
            <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl">
              <h3 className="text-sm font-bold text-gray-900">Delete {form.modelName || form.modelId}?</h3>
              <p className="mt-1.5 text-xs text-gray-600">
                Nothing references this device, so deleting it is safe. It cannot be undone.
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setConfirmDelete(false)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button onClick={doDelete} disabled={deleting}
                  className="flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                  {deleting ? <RubyGemMark size={13} drawing color="currentColor" /> : <Trash2 size={13} />}
                  Delete permanently
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* DRAFT SAVED — a pill, not a dialog. */}
      {saved?.kind === 'draft' && (
        <div className="pointer-events-none fixed bottom-6 left-1/2 z-[10000] -translate-x-1/2">
          <div className="dq-rise flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-4 py-2.5 shadow-lg">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-[11px] font-bold text-white">
              ✓
            </span>
            <span className="text-[12.5px] text-stone-700">
              Draft saved — keep going, or come back to it later.
            </span>
          </div>
        </div>
      )}

      {/* PUBLISHED — the end of a piece of work, so it asks what next rather
          than choosing for the operator. */}
      {saved?.kind === 'published' && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-stone-900/50" onClick={() => setSaved(null)} />
          <div className="dq-pop relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-2xl">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
              <span className="dq-tick text-2xl font-bold text-emerald-600">✓</span>
            </div>
            <h3 className="text-base font-bold text-gray-900">Device published</h3>
            <p className="mt-1 text-[12.5px] text-gray-500">
              {form.modelName} is live in the catalog.
            </p>
            <div className="mt-5 space-y-2">
              {saved.sellable && (
                <button
                  onClick={() => onSaved({ modelId: saved.modelId, sellable: true })}
                  className="w-full min-h-[42px] rounded-lg text-sm font-semibold text-white"
                  style={{ background: '#DC143C' }}
                >
                  Set selling terms →
                </button>
              )}
              <button
                onClick={() => onSaved({ modelId: saved.modelId, sellable: false })}
                className="w-full min-h-[42px] rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Back to stock
              </button>
              <button
                onClick={() => setSaved(null)}
                className="w-full min-h-[38px] text-[12.5px] text-gray-500 hover:text-gray-800"
              >
                Keep editing
              </button>
            </div>
          </div>
        </div>
      )}

      {newVendor !== null && (
        <VendorQuickCreate
          initialName={newVendor}
          onClose={() => setNewVendor(null)}
          onCreated={(v) => {
            // Added to the SAME registry Inventory → Vendors reads, so the
            // vendor exists everywhere the moment it is created here — not a
            // local string that looks like a vendor until somebody checks.
            setVendorList((prev) => [...prev.filter((x) => x.vendorId !== v.vendorId), v]);
            set('vendor.supplierName', v.name);
            if (v.leadTimeDays && !form.vendor?.leadTimeDays) set('vendor.leadTimeDays', v.leadTimeDays);
            setNewVendor(null);
          }}
        />
      )}
    </div>
  );
};

/**
 * CREATE A VENDOR WITHOUT LEAVING THE FORM.
 *
 * Adding a device and discovering its supplier is not in the registry used to
 * mean abandoning a half-filled form, going to Inventory → Vendors, and coming
 * back to start again. This writes the real vendor record — `upsertVendor`, the
 * same endpoint that page posts to — so nothing here is a shortcut that creates
 * a lesser vendor.
 *
 * Deliberately the SHORT form: name, lead time, payment terms. A vendor has a
 * dozen more fields (contacts, address, website, notes) and asking for them
 * mid-device-entry is how an operator loses the thread. The record is real and
 * complete-able; Inventory → Vendors edits the rest.
 */
const VendorQuickCreate: React.FC<{
  initialName: string;
  onClose: () => void;
  onCreated: (v: { vendorId: string; name: string; leadTimeDays?: number }) => void;
}> = ({ initialName, onClose, onCreated }) => {
  const [name, setName] = useState(initialName);
  const [leadTimeDays, setLeadTimeDays] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await inventoryApi.upsertVendor({
        name: name.trim(),
        active: true,
        ...(leadTimeDays.trim() ? { leadTimeDays: Number(leadTimeDays) } : {}),
        ...(paymentTerms.trim() ? { paymentTerms: paymentTerms.trim() } : {}),
      });
      onCreated(res.vendor);
    } catch (e: any) {
      setError(e?.message || 'Could not create the vendor');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-stone-900/60" onClick={() => !saving && onClose()} />
      <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white p-5 shadow-2xl">
        <h3 className="text-base font-bold text-gray-900">New vendor</h3>
        <p className="mt-1 text-[11.5px] text-gray-500">
          Written to the vendor registry. You can fill in contacts and address later in
          Inventory → Vendors.
        </p>

        {error && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
            {error}
          </div>
        )}

        <label className="mt-3 mb-1 block text-xs font-semibold text-gray-700">Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-stone-800"
        />

        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-700">Lead time (days)</label>
            <input
              type="number" min="0" value={leadTimeDays}
              onChange={(e) => setLeadTimeDays(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-stone-800"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-700">Payment terms</label>
            <input
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              placeholder="net-30"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-stone-800"
            />
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button" onClick={onClose} disabled={saving}
            className="min-h-[40px] rounded-lg border border-gray-300 px-4 text-sm text-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button" onClick={create} disabled={!name.trim() || saving}
            className="min-h-[40px] rounded-lg px-4 text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: '#DC143C' }}
          >
            {saving ? 'Creating…' : 'Create vendor'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeviceEditor;
